import React, { useState, useEffect } from 'react';
import { 
  X, Plus, Trash2, Image as ImageIcon, FileText, Video, Globe, Sparkles, Link, 
  CheckCircle2, ChevronDown, Zap, Lightbulb, Target, Shield, AlertCircle, CircleDot, Layers, Tag,
  Key, Lock, ShieldCheck, ListFilter, Menu
} from 'lucide-react';
import { useTheme } from './ThemeContext';
import { DYNAMIC_VARIABLE_GROUPS, ALL_DYNAMIC_VARIABLES, getDynamicListItems } from './dynamicVariables';
import { collection, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, app } from '../../firebase/FirebaseCredential';

export const InspectorDrawer = ({
  selectedNode,
  nodes = [],
  contactAttributes = [],
  availableWhatsappFlows = [],
  liveCollections = {},
  onUpdateNode,
  onDeleteNode,
  onClose,
  tenantId,
}) => {
  const { isDark } = useTheme();
  const id = selectedNode?.id;
  const type = selectedNode?.type;
  const data = selectedNode?.data || {};
  const isTrigger = id === 'trigger_1' || Boolean(id?.startsWith('trigger_'));

  const [approvedTemplates, setApprovedTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [flowsList, setFlowsList] = useState(availableWhatsappFlows || []);
  const [activeVarCategory, setActiveVarCategory] = useState('Classes');

  useEffect(() => {
    if (availableWhatsappFlows && availableWhatsappFlows.length > 0) {
      setFlowsList(availableWhatsappFlows);
    } else if (tenantId) {
      fetch(`/app/${tenantId}/api/whatsapp-flows`)
        .then(res => res.json())
        .then(data => {
          if (Array.isArray(data)) setFlowsList(data);
        })
        .catch(() => {});
    }
  }, [tenantId, availableWhatsappFlows]);

  // Flow-collected variables from other nodes in canvas
  const flowVariables = React.useMemo(() => {
    const list = [];
    (nodes || []).forEach((n) => {
      if (n.id !== id && n.data?.variable && !list.includes(n.data.variable)) {
        list.push(n.data.variable);
      }
      if (Array.isArray(n.data?.mappings)) {
        n.data.mappings.forEach((m) => {
          if (m.variable && !list.includes(m.variable)) {
            list.push(m.variable);
          }
        });
      }
    });
    return list;
  }, [nodes, id]);

  // Specific list of API-extracted variables
  const apiVariables = React.useMemo(() => {
    const list = [];
    (nodes || []).forEach((n) => {
      if (Array.isArray(n.data?.mappings)) {
        n.data.mappings.forEach((m) => {
          if (m.variable && !list.includes(m.variable)) {
            list.push(m.variable);
          }
        });
      }
    });
    return list;
  }, [nodes]);

  // Specific list of question-collected variables
  const questionVariables = React.useMemo(() => {
    const list = [];
    (nodes || []).forEach((n) => {
      if (n.type === 'questionNode' && n.data?.variable && !list.includes(n.data.variable)) {
        list.push(n.data.variable);
      }
    });
    return list;
  }, [nodes]);

  // Combined list of contact attributes and flow variables (unique)
  const allAttributes = React.useMemo(() => {
    const set = new Set([...(contactAttributes || []), ...flowVariables]);
    return Array.from(set).filter(Boolean);
  }, [contactAttributes, flowVariables]);

  const insertVariableIntoMessage = (varText) => {
    const currentBody = data.body || '';
    const textarea = document.getElementById(`msg-body-textarea-${id}`);
    if (textarea) {
      const start = textarea.selectionStart ?? currentBody.length;
      const end = textarea.selectionEnd ?? currentBody.length;
      const newText = currentBody.substring(0, start) + varText + currentBody.substring(end);
      handleChange('body', newText);
      setTimeout(() => {
        textarea.focus();
        const nextPos = start + varText.length;
        textarea.setSelectionRange(nextPos, nextPos);
      }, 50);
    } else {
      handleChange('body', currentBody + (currentBody ? ' ' : '') + varText);
    }
  };

  useEffect(() => {
    if (type === 'messageNode') {
      setLoadingTemplates(true);
      const loadApprovedTemplates = async () => {
        const fetched = [];
        const seenNames = new Set();

        // 1. Try Firebase Cloud Function getWhatsAppTemplates
        if (tenantId && tenantId !== 'default') {
          try {
            const functions = getFunctions(app, 'asia-south1');
            const getWhatsAppTemplatesCF = httpsCallable(functions, 'getWhatsAppTemplates');
            const response = await getWhatsAppTemplatesCF({ companyId: tenantId });
            const result = response.data;
            if (result && result.success && Array.isArray(result.data)) {
              result.data.forEach((item) => {
                const name = item.template_name || item.name;
                if (name && !seenNames.has(name)) {
                  seenNames.add(name);
                  fetched.push({
                    id: item.template_id || item.id || name,
                    name: name,
                    category: item.category || 'UTILITY',
                    body: item.body_text || '',
                    header_type: item.header_type || 'NONE',
                    buttons: item.buttons || [],
                    language: item.language || 'en',
                    variables: item.variables || [],
                  });
                }
              });
            }
          } catch (cfErr) {
            console.warn('Could not fetch via getWhatsAppTemplates Cloud Function:', cfErr);
          }
        }

        // 2. Fetch from Firestore company templates
        if (tenantId && tenantId !== 'default') {
          try {
            const snap = await getDocs(
              collection(db, 'companyprofile', tenantId, 'companysettings', 'whatsappsettings', 'whatsapptemplate')
            );
            snap.forEach((d) => {
              const data = d.data();
              const name = data.template_name || data.name;
              if (name && !seenNames.has(name)) {
                seenNames.add(name);
                fetched.push({
                  id: d.id,
                  name: name,
                  category: data.template_category || data.category || 'UTILITY',
                  body: data.template_body || data.body_text || '',
                  header_type: data.header_type || 'NONE',
                  buttons: data.buttons || [],
                  language: data.language || 'en',
                  variables: data.fields || [],
                });
              }
            });
          } catch (e) {
            console.warn('Could not fetch from company whatsapptemplate in Firestore:', e);
          }
        }

        // 3. Fetch from global Firestore templates: smsmojo/whatsappconfig/templates
        try {
          const globalSnap = await getDocs(collection(db, 'smsmojo', 'whatsappconfig', 'templates'));
          globalSnap.forEach((d) => {
            const data = d.data();
            const name = data.template_name || d.id;
            if (name && !seenNames.has(name)) {
              seenNames.add(name);
              fetched.push({
                id: d.id,
                name: name,
                category: data.template_category || 'UTILITY',
                body: data.template_body || data.body_text || '',
                header_type: data.header_type || 'NONE',
                buttons: data.buttons || [],
                language: data.language || 'en',
                variables: data.fields || [],
              });
            }
          });
        } catch (e) {
          console.warn('Could not fetch global templates from Firestore:', e);
        }

        // 4. Guaranteed default registered Bench templates
        const defaultTemplates = [
          {
            id: 'enquiry_new',
            name: 'enquiry_new',
            category: 'UTILITY',
            body: 'Hi {{name}},\n\nYour enquiry has been successfully received by {{gym_name}}.\n\nOur team will review your request and get back to you shortly.\n\nThank you.\nTeam Bench',
            header_type: 'NONE',
            buttons: [],
            variables: [{ name: 'name' }, { name: 'gym_name' }],
          },
          {
            id: 'member_registeration',
            name: 'member_registeration',
            category: 'UTILITY',
            body: 'Welcome to {{gym_name}}, {{member_name}}!\n\nYour membership for {{plan_name}} has been activated successfully.\n\nValidity: {{start_date}} to {{expiry_date}}.\n\nLet\'s achieve your fitness goals together!',
            header_type: 'NONE',
            buttons: [],
            variables: [{ name: 'gym_name' }, { name: 'member_name' }, { name: 'plan_name' }, { name: 'start_date' }, { name: 'expiry_date' }],
          },
          {
            id: 'class_booking_confirmation',
            name: 'class_booking_confirmation',
            category: 'UTILITY',
            body: 'Hi {{member_name}},\n\nYour class booking at {{gym_name}} is confirmed.\n\nClass: {{class_name}}\nDate: {{class_date}}\nTime: {{class_time}}\nTrainer: {{trainer_name}}\n\nPlease arrive 10 minutes early. See you at the session.',
            header_type: 'NONE',
            buttons: [],
            variables: [{ name: 'member_name' }, { name: 'gym_name' }, { name: 'class_name' }, { name: 'class_date' }, { name: 'class_time' }, { name: 'trainer_name' }],
          },
          {
            id: 'payment_link',
            name: 'payment_link',
            category: 'UTILITY',
            body: 'Hi {{member_name}},\n\nYour payment of ₹{{amount}} for {{plan_name}} is pending.\n\nPlease complete your payment using the link below:\n{{payment_link}}\n\nThank you,\n{{gym_name}}',
            header_type: 'NONE',
            buttons: [],
            variables: [{ name: 'member_name' }, { name: 'plan_name' }, { name: 'amount' }, { name: 'payment_link' }, { name: 'gym_name' }],
          },
          {
            id: 'successfullpayment',
            name: 'successfullpayment',
            category: 'UTILITY',
            body: 'Hi,\n\nWe have received your payment of ₹{{amount}} for {{brandname}}.\n\nThank you for choosing us!',
            header_type: 'NONE',
            buttons: [],
            variables: [{ name: 'amount' }, { name: 'brandname' }],
          },
        ];

        defaultTemplates.forEach((t) => {
          if (!seenNames.has(t.name)) {
            seenNames.add(t.name);
            fetched.push(t);
          }
        });

        setApprovedTemplates(fetched);
        setLoadingTemplates(false);
      };

      loadApprovedTemplates();
    }
  }, [id, type, tenantId]);

  if (!selectedNode) return null;

  const inputCls = `w-full rounded-lg border px-3 py-2 text-xs focus:outline-none transition-colors ${
    isDark
      ? 'border-slate-700 bg-slate-900 text-white placeholder-slate-500 focus:border-[#dc2742]'
      : 'border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-[#dc2742] focus:ring-1 focus:ring-[#dc2742]/20'
  }`;

  const labelCls = `block text-[11px] font-semibold mb-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`;

  const handleChange = (key, value) => {
    onUpdateNode(id, { ...data, [key]: value });
  };

  const handleAddButton = () => {
    const buttons = data.buttons || [];
    if (buttons.length >= 3) return;
    const newButtons = [
      ...buttons,
      { id: `btn_${Date.now()}`, title: '' }
    ];
    handleChange('buttons', newButtons);
  };

  const handleUpdateButton = (idx, title) => {
    const buttons = [...(data.buttons || [])];
    buttons[idx] = { ...buttons[idx], title };
    handleChange('buttons', buttons);
  };

  const handleRemoveButton = (idx) => {
    const buttons = (data.buttons || []).filter((_, i) => i !== idx);
    handleChange('buttons', buttons);
  };

  const handleAddCustomListItem = () => {
    const items = [...(data.listCustomItems || [])];
    if (items.length >= 10) return;
    items.push({
      id: `item_${Date.now()}`,
      title: `Option ${items.length + 1}`,
      description: '',
    });
    handleChange('listCustomItems', items);
  };

  const handleUpdateCustomListItem = (idx, field, val) => {
    const items = [...(data.listCustomItems || [])];
    items[idx] = { ...items[idx], [field]: val };
    handleChange('listCustomItems', items);
  };

  const handleRemoveCustomListItem = (idx) => {
    const items = (data.listCustomItems || []).filter((_, i) => i !== idx);
    handleChange('listCustomItems', items);
  };

  const handleAddKeyword = (kw) => {
    if (!kw.trim()) return;
    const keywords = data.keywords || [];
    if (!keywords.includes(kw.trim().toLowerCase())) {
      handleChange('keywords', [...keywords, kw.trim().toLowerCase()]);
    }
  };

  const handleRemoveKeyword = (idx) => {
    const keywords = (data.keywords || []).filter((_, i) => i !== idx);
    handleChange('keywords', keywords);
  };

  return (
    <div
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        bottom: 0,
        zIndex: 30,
        width: 380,
        minWidth: 380,
        maxWidth: 380,
        backgroundColor: isDark ? '#1a1b1e' : '#ffffff',
        borderLeft: isDark ? '1px solid #2c2e33' : '1px solid #e2e8f0',
        color: isDark ? '#f1f5f9' : '#1e293b',
        boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px',
        fontSize: '12px',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: '14px', borderBottom: isDark ? '1px solid #2c2e33' : '1px solid #f1f5f9' }}>
        <div>
          <div style={{ fontSize: '10px', fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: '0.05em', color: isDark ? '#94a3b8' : '#64748b' }}>Node Properties</div>
          <div style={{ fontSize: '15px', fontWeight: 700, textTransform: 'capitalize', color: isDark ? '#ffffff' : '#0f172a' }}>{type.replace('Node', '')} Settings</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {!isTrigger && onDeleteNode && (
            <button
              onClick={() => onDeleteNode(id)}
              title="Delete this block"
              style={{
                background: 'none',
                border: 'none',
                borderRadius: '8px',
                padding: '6px',
                cursor: 'pointer',
                color: '#f87171',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.15s ease',
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#fee2e2'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={onClose}
            title="Close Settings"
            style={{
              background: 'none',
              border: 'none',
              borderRadius: '8px',
              padding: '6px',
              cursor: 'pointer',
              color: isDark ? '#94a3b8' : '#64748b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark ? '#2c2e33' : '#f1f5f9';
              e.currentTarget.style.color = isDark ? '#ffffff' : '#0f172a';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = isDark ? '#94a3b8' : '#64748b';
            }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div style={{ paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {/* Label */}
        <div>
          <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: isDark ? '#cbd5e1' : '#334155' }}>Step Label</label>
          <input
            type="text"
            value={data.label || ''}
            onChange={(e) => handleChange('label', e.target.value)}
            style={{
              width: '100%',
              borderRadius: '8px',
              border: '1px solid #cbd5e1',
              padding: '8px 12px',
              fontSize: '12px',
              backgroundColor: isDark ? '#141517' : '#ffffff',
              color: isDark ? '#ffffff' : '#0f172a',
              outline: 'none',
              transition: 'border-color 0.15s ease',
            }}
            onFocus={(e) => e.currentTarget.style.borderColor = '#9333ea'}
            onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
          />
        </div>

        {/* Trigger Node Specifics */}
        {type === 'triggerNode' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: isDark ? '#cbd5e1' : '#334155' }}>Trigger Mode</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
                <button
                  type="button"
                  onClick={() => handleChange('triggerType', 'KEYWORD')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 8px',
                    borderRadius: '8px',
                    border: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? '2px solid #9333ea' : '1px solid #e2e8f0',
                    backgroundColor: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? (isDark ? '#3b0764' : '#faf5ff') : (isDark ? '#25262b' : '#f8fafc'),
                    color: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? (isDark ? '#d8b4fe' : '#7e22ce') : (isDark ? '#94a3b8' : '#64748b'),
                    fontWeight: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? '0 0 0 1px rgba(147, 51, 234, 0.2)' : 'none',
                  }}
                >
                  <Target className="h-4 w-4" style={{ marginBottom: 4, color: (data.triggerType || 'KEYWORD') !== 'FALLBACK' ? '#9333ea' : '#94a3b8' }} />
                  <span style={{ fontSize: '11px' }}>Keyword Trigger</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('triggerType', 'FALLBACK')}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '12px 8px',
                    borderRadius: '8px',
                    border: data.triggerType === 'FALLBACK' ? '2px solid #9333ea' : '1px solid #e2e8f0',
                    backgroundColor: data.triggerType === 'FALLBACK' ? (isDark ? '#3b0764' : '#faf5ff') : (isDark ? '#25262b' : '#f8fafc'),
                    color: data.triggerType === 'FALLBACK' ? (isDark ? '#d8b4fe' : '#7e22ce') : (isDark ? '#94a3b8' : '#64748b'),
                    fontWeight: data.triggerType === 'FALLBACK' ? 700 : 500,
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    boxShadow: data.triggerType === 'FALLBACK' ? '0 0 0 1px rgba(147, 51, 234, 0.2)' : 'none',
                  }}
                >
                  <Shield className="h-4 w-4" style={{ marginBottom: 4, color: data.triggerType === 'FALLBACK' ? '#9333ea' : '#94a3b8' }} />
                  <span style={{ fontSize: '11px' }}>Default / Fallback</span>
                </button>
              </div>
            </div>

            {data.triggerType === 'FALLBACK' ? (
              <div style={{
                padding: '12px',
                borderRadius: '10px',
                border: '1px solid #ddd6fe',
                backgroundColor: isDark ? '#2e1065' : '#faf5ff',
                color: isDark ? '#e9d5ff' : '#581c87',
                fontSize: '11px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}>
                <div style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Shield className="h-4 w-4" style={{ color: '#a855f7' }} />
                  <span>Catch-All Default Flow</span>
                </div>
                <p style={{ margin: 0, lineHeight: 1.5, opacity: 0.9 }}>
                  This flow triggers automatically when a customer's WhatsApp message does <strong>not</strong> match any keywords in your other active flows.
                </p>
                <div style={{ fontSize: '10px', opacity: 0.85, paddingTop: '6px', borderTop: '1px solid rgba(168, 85, 247, 0.2)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Zap className="h-3.5 w-3.5" style={{ color: '#a855f7', flexShrink: 0 }} />
                  <span>Note: Only <strong>one</strong> Default Fallback Flow can be active at a time per account.</span>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: isDark ? '#cbd5e1' : '#334155' }}>Inbound Trigger Keywords</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="e.g. price, offer, hi, hello"
                    id="kw_input"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddKeyword(e.target.value);
                        e.target.value = '';
                      }
                    }}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      borderRadius: '8px',
                      border: '1px solid #cbd5e1',
                      fontSize: '12px',
                      backgroundColor: isDark ? '#141517' : '#ffffff',
                      color: isDark ? '#ffffff' : '#0f172a',
                      outline: 'none',
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#9333ea'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#cbd5e1'}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const input = document.getElementById('kw_input');
                      if (input && input.value) {
                        handleAddKeyword(input.value);
                        input.value = '';
                      }
                    }}
                    style={{
                      borderRadius: '8px',
                      backgroundColor: '#9333ea',
                      color: '#ffffff',
                      border: 'none',
                      padding: '8px 16px',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7e22ce'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9333ea'}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', paddingTop: '4px' }}>
                  {(data.keywords || []).map((kw, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        backgroundColor: isDark ? '#3b0764' : '#faf5ff',
                        border: isDark ? '1px solid #581c87' : '1px solid #e9d5ff',
                        color: isDark ? '#e9d5ff' : '#6b21a8',
                        fontSize: '11px',
                        fontWeight: 600,
                        fontFamily: 'ui-monospace, monospace',
                      }}
                    >
                      <span>{kw}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveKeyword(idx)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#a855f7',
                          padding: '0 2px',
                          fontSize: '13px',
                          lineHeight: 1,
                          fontWeight: 700,
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = '#ef4444'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#a855f7'}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Message Node Specifics */}
        {type === 'messageNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Message Format</label>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <button
                  type="button"
                  onClick={() => handleChange('messageType', 'custom')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold border text-center transition-all ${
                    (data.messageType || 'custom') === 'custom'
                      ? 'bg-emerald-600 text-white border-emerald-500 shadow-sm'
                      : (isDark ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200')
                  }`}
                >
                  Custom Message
                </button>
                <button
                  type="button"
                  onClick={() => handleChange('messageType', 'template')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold border text-center transition-all ${
                    data.messageType === 'template'
                      ? 'bg-amber-600 text-white border-amber-500 shadow-sm'
                      : (isDark ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200')
                  }`}
                >
                  Meta Template
                </button>
              </div>
            </div>

            {data.messageType === 'template' ? (
              (() => {
                const selectedTemplate = approvedTemplates.find(t => t.name === (data.template_name || '')) || null;
                const currentHeaderType = (data.headerType && data.headerType !== 'NONE')
                  ? data.headerType
                  : (selectedTemplate?.header_type || 'NONE');

                const bodyContent = data.body || selectedTemplate?.body || '';
                const bodyMatches = bodyContent.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || [];
                const bodyPlaceholders = Array.from(new Set(bodyMatches.map(m => m.replace(/[\{\}\s]/g, ''))));

                const headerContent = data.header_text || selectedTemplate?.header_text || '';
                const headerMatches = headerContent.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || [];
                const headerPlaceholders = Array.from(new Set(headerMatches.map(m => m.replace(/[\{\}\s]/g, ''))));

                const isMediaHeader = ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(currentHeaderType);

                const dynamicButtons = [];
                if (Array.isArray(selectedTemplate?.buttons)) {
                  selectedTemplate.buttons.forEach((b, idx) => {
                    const isDynUrl = (b.type === 'URL' || b.type === 'url') && (b.url_type === 'DYNAMIC' || (b.url && b.url.includes('{{')));
                    const isCopyCode = b.type === 'COPY_CODE' || b.type === 'copy_code';
                    if (isDynUrl || isCopyCode) {
                      dynamicButtons.push({
                        index: idx,
                        title: b.text || b.title || `Button ${idx + 1}`,
                        type: b.type,
                        url: b.url || '',
                        url_type: b.url_type,
                      });
                    }
                  });
                }
                const carouselCards = (data.carousel_cards && data.carousel_cards.length > 0)
                  ? data.carousel_cards
                  : (selectedTemplate?.carousel_cards || []);

                return (
                  <div className="space-y-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
                    {/* Template Selector */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className={labelCls}>Select Approved Template</label>
                        {selectedTemplate && (
                          <div className="flex items-center gap-1">
                            {carouselCards.length > 0 && (
                              <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400 border border-purple-500/30 flex items-center gap-1">
                                <Layers className="h-2.5 w-2.5" />
                                Carousel
                              </span>
                            )}
                            <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
                              {selectedTemplate.category || 'APPROVED'}
                            </span>
                          </div>
                        )}
                      </div>
                      <select
                        value={data.template_name || ''}
                        onChange={(e) => {
                          const selName = e.target.value;
                          const found = approvedTemplates.find(t => t.name === selName);
                          if (found) {
                            const extractedBtns = [];
                            if (Array.isArray(found.buttons) && found.buttons.length > 0) {
                              found.buttons.forEach((b, i) => {
                                extractedBtns.push({ id: `btn_${i + 1}`, title: b.text || b.title || `Option ${i + 1}` });
                              });
                            } else if (Array.isArray(found.carousel_cards)) {
                              found.carousel_cards.forEach((card, cIdx) => {
                                if (Array.isArray(card.buttons)) {
                                  card.buttons.forEach((b, bIdx) => {
                                    extractedBtns.push({
                                      id: `card_${cIdx}_btn_${bIdx + 1}`,
                                      title: `Card ${cIdx + 1}: ${b.text || b.title || 'Option'}`
                                    });
                                  });
                                }
                              });
                            }
                            const ht = found.header_type || 'NONE';
                            onUpdateNode(id, {
                              ...data,
                              template_name: found.name,
                              template_id: found.id || found.template_id || '',
                              template_language: found.language || 'en',
                              body: found.body || data.body || '',
                              header_text: found.header_text || '',
                              headerType: ht,
                              carousel_cards: found.carousel_cards || [],
                              mediaUrl: data.mediaUrl || '',
                              template_variables: data.template_variables || {},
                              template_button_variables: data.template_button_variables || {},
                              carousel_media_variables: data.carousel_media_variables || {},
                              carousel_body_variables: data.carousel_body_variables || {},
                              buttons: extractedBtns.length ? extractedBtns : (data.buttons || []),
                            });
                          } else {
                            handleChange('template_name', selName);
                          }
                        }}
                        className={`${inputCls} focus:border-amber-500 font-medium`}
                      >
                        <option value="">-- Choose Approved Template --</option>
                        {approvedTemplates.map(tmpl => (
                          <option key={tmpl.id} value={tmpl.name}>
                            {tmpl.name} ({tmpl.category})
                          </option>
                        ))}
                      </select>
                      {loadingTemplates && <div className="text-[10px] text-amber-500 mt-1">Loading approved templates...</div>}
                    </div>

                    {/* Media Header Section */}
                    {isMediaHeader && (
                      <div className={`p-2.5 rounded-lg border space-y-2 ${
                        isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1.5 text-xs font-bold text-amber-500">
                            {currentHeaderType === 'IMAGE' && <ImageIcon className="h-3.5 w-3.5 text-emerald-400" />}
                            {currentHeaderType === 'DOCUMENT' && <FileText className="h-3.5 w-3.5 text-blue-400" />}
                            {currentHeaderType === 'VIDEO' && <Video className="h-3.5 w-3.5 text-purple-400" />}
                            <span>Header Media ({currentHeaderType})</span>
                          </div>
                          <span className="text-[10px] text-amber-400 font-semibold">Meta Required</span>
                        </div>

                        <div>
                          <label className={labelCls}>Media URL or Dynamic Variable</label>
                          <input
                            type="text"
                            placeholder="https://example.com/banner.png or {{media_url}}"
                            value={data.mediaUrl || ''}
                            onChange={(e) => handleChange('mediaUrl', e.target.value)}
                            className={`${inputCls} font-mono`}
                          />
                        </div>

                        {/* Quick Connect Variable to Media Header */}
                        {(apiVariables.length > 0 || allAttributes.length > 0) && (
                          <div className="space-y-1 pt-1">
                            <span className="text-[10px] text-slate-400 font-semibold flex items-center gap-1">
                              <Zap className="h-3 w-3 text-amber-400" />
                              Connect Variable to Media:
                            </span>
                            <div className="flex flex-wrap gap-1 max-h-16 overflow-y-auto">
                              {apiVariables.map(v => (
                                <button
                                  key={v}
                                  type="button"
                                  onClick={() => handleChange('mediaUrl', `{{${v}}}`)}
                                  className={`rounded px-1.5 py-0.5 text-[10px] font-mono border transition-colors ${
                                    data.mediaUrl === `{{${v}}}`
                                      ? 'bg-amber-500 text-white border-amber-600 font-bold'
                                      : (isDark ? 'bg-slate-800 text-amber-300 border-slate-700 hover:bg-slate-700' : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100')
                                  }`}
                                  title={`Use {{${v}}} from API response`}
                                >
                                  + {`{{${v}}}`}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Image Preview if Static URL */}
                        {data.mediaUrl && !data.mediaUrl.startsWith('{{') && (
                          <div className="rounded border overflow-hidden max-h-24 bg-black/10 flex items-center justify-center p-1">
                            {currentHeaderType === 'IMAGE' ? (
                              <img src={data.mediaUrl} alt="Preview" className="max-h-20 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                            ) : (
                              <div className="flex items-center gap-1.5 text-[11px] text-slate-400 truncate">
                                <FileText className="h-3.5 w-3.5" />
                                <span className="truncate">{data.mediaUrl}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Header Text Placeholders (if header has variables) */}
                    {headerPlaceholders.length > 0 && (
                      <div className={`p-2.5 rounded-lg border space-y-2 ${
                        isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                        <div className="text-xs font-bold text-amber-500">Header Text Variables</div>
                        {headerPlaceholders.map(ph => {
                          const currentVal = (data.template_header_variables || {})[ph] || '';
                          return (
                            <div key={ph} className="flex gap-1.5 items-center">
                              <span className="text-[11px] font-mono font-bold text-slate-400 w-16">{`{{${ph}}}`}:</span>
                              <input
                                type="text"
                                placeholder={`e.g. {{contact.first_name}}`}
                                value={currentVal}
                                onChange={(e) => {
                                  const updated = { ...(data.template_header_variables || {}), [ph]: e.target.value };
                                  handleChange('template_header_variables', updated);
                                }}
                                className={`${inputCls} font-mono`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Variables Mapping Section (Supports Both Dynamic & Static) */}
                    {bodyPlaceholders.length > 0 && (
                      <div className={`p-2.5 rounded-lg border space-y-2.5 ${
                        isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-amber-500 flex items-center gap-1">
                            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                            Map Variables (Dynamic & Static) ({bodyPlaceholders.length})
                          </span>
                          <span className="text-[10px] text-slate-400">Fixed text or {`{{variable}}`}</span>
                        </div>

                        <div className="space-y-2">
                          {bodyPlaceholders.map((ph) => {
                            const currentVal = (data.template_variables || {})[ph] || '';
                            const isDynamic = currentVal && currentVal.includes('{{');
                            const isStatic = currentVal && !currentVal.includes('{{');

                            return (
                              <div key={ph} className={`p-2 rounded-lg border space-y-1.5 ${
                                isDark ? 'bg-slate-800/60 border-slate-700/80' : 'bg-slate-50 border-slate-200'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-mono font-bold text-emerald-500">
                                    Placeholder {`{{${ph}}}`}
                                  </span>
                                  {isDynamic && (
                                    <span className="text-[10px] font-mono text-cyan-400 truncate max-w-[150px] bg-cyan-500/10 px-1.5 py-0.5 rounded border border-cyan-500/20 flex items-center gap-1">
                                      <Sparkles className="h-2.5 w-2.5 text-cyan-400" />
                                      Dynamic: {currentVal}
                                    </span>
                                  )}
                                  {isStatic && (
                                    <span className="text-[10px] font-sans text-emerald-400 truncate max-w-[150px] bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-500/20 flex items-center gap-1 font-semibold">
                                      <Tag className="h-2.5 w-2.5 text-emerald-400" />
                                      Static: "{currentVal}"
                                    </span>
                                  )}
                                  {!currentVal && (
                                    <span className="text-[10px] text-amber-400 italic bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-500/20">
                                      Unmapped
                                    </span>
                                  )}
                                </div>

                                <div className="flex gap-1.5 items-center">
                                  <input
                                    type="text"
                                    placeholder="Enter static text or pick a variable..."
                                    value={currentVal}
                                    onChange={(e) => {
                                      const updated = { ...(data.template_variables || {}), [ph]: e.target.value };
                                      handleChange('template_variables', updated);
                                    }}
                                    className={`${inputCls} font-mono text-[11px] flex-1`}
                                  />

                                  {/* Variable Picker Dropdown */}
                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        const updated = { ...(data.template_variables || {}), [ph]: e.target.value };
                                        handleChange('template_variables', updated);
                                      }
                                    }}
                                    title="Choose a dynamic variable"
                                    className={`w-32 shrink-0 rounded-lg border px-2 py-1 text-[11px] font-medium focus:outline-none transition-colors ${
                                      isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-800'
                                    }`}
                                  >
                                    <option value="">+ Variable...</option>
                                    {apiVariables.length > 0 && (
                                      <optgroup label="API Response Data">
                                        {apiVariables.map((v) => (
                                          <option key={v} value={`{{${v}}}`}>{`{{${v}}}`}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {questionVariables.length > 0 && (
                                      <optgroup label="Question Inputs">
                                        {questionVariables.map((v) => (
                                          <option key={v} value={`{{${v}}}`}>{`{{${v}}}`}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {DYNAMIC_VARIABLE_GROUPS.map((grp) => (
                                      <optgroup key={grp.group} label={grp.group}>
                                        {grp.variables.map((v) => (
                                          <option key={v.value} value={`{{${v.value}}}`}>
                                            {v.label} ({`{{${v.value}}}`})
                                          </option>
                                        ))}
                                      </optgroup>
                                    ))}
                                    <optgroup label="Contact Fields">
                                      <option value="{{contact.name}}">contact.name</option>
                                      <option value="{{contact.first_name}}">contact.first_name</option>
                                      <option value="{{contact.phone}}">contact.phone</option>
                                    </optgroup>
                                    {contactAttributes.length > 0 && (
                                      <optgroup label="Contact Attributes">
                                        {contactAttributes.map((attr) => (
                                          <option key={attr} value={`{{${attr}}}`}>{attr}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                  </select>

                                  {currentVal && (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = { ...(data.template_variables || {}) };
                                        delete updated[ph];
                                        handleChange('template_variables', updated);
                                      }}
                                      title="Clear value"
                                      className={`p-1 rounded hover:bg-red-500/20 text-slate-400 hover:text-red-400 transition-colors`}
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  )}
                                </div>

                                {/* Quick chips for API variables */}
                                {apiVariables.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pt-0.5">
                                    {apiVariables.map((v) => (
                                      <button
                                        key={v}
                                        type="button"
                                        onClick={() => {
                                          const updated = { ...(data.template_variables || {}), [ph]: `{{${v}}}` };
                                          handleChange('template_variables', updated);
                                        }}
                                        className={`rounded px-1.5 py-0.2 text-[9px] font-mono border transition-all ${
                                          currentVal === `{{${v}}}`
                                            ? 'bg-emerald-500 text-white border-emerald-600 font-bold'
                                            : (isDark ? 'bg-slate-900/80 text-slate-300 border-slate-700 hover:bg-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:bg-emerald-50')
                                        }`}
                                      >
                                        + {`{{${v}}}`}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Dynamic Button Variables (Dynamic URL / Copy Code buttons) */}
                    {dynamicButtons.length > 0 && (
                      <div className={`p-2.5 rounded-lg border space-y-2.5 ${
                        isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-amber-500 flex items-center gap-1">
                            <Link className="h-3.5 w-3.5 text-amber-400" />
                            Dynamic Button Variables ({dynamicButtons.length})
                          </span>
                          <span className="text-[10px] text-slate-400">URL / Code Parameter</span>
                        </div>

                        <div className="space-y-2">
                          {dynamicButtons.map((btn) => {
                            const currentVal = (data.template_button_variables || {})[btn.index] || '';
                            return (
                              <div key={btn.index} className={`p-2 rounded-lg border space-y-1.5 ${
                                isDark ? 'bg-slate-800/60 border-slate-700/80' : 'bg-slate-50 border-slate-200'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-mono font-bold text-emerald-500 flex items-center gap-1.5">
                                    <CircleDot className="h-3 w-3" />
                                    {btn.title}
                                  </span>
                                  <span className="text-[10px] font-mono text-slate-400 truncate max-w-[140px]">
                                    {btn.url || btn.type}
                                  </span>
                                </div>

                                <div className="flex gap-1.5">
                                  <input
                                    type="text"
                                    placeholder="e.g. {{tracking_link}} or {{order_id}}"
                                    value={currentVal}
                                    onChange={(e) => {
                                      const updated = { ...(data.template_button_variables || {}), [btn.index]: e.target.value };
                                      handleChange('template_button_variables', updated);
                                    }}
                                    className={`${inputCls} font-mono text-[11px]`}
                                  />

                                  <select
                                    value=""
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        const updated = { ...(data.template_button_variables || {}), [btn.index]: e.target.value };
                                        handleChange('template_button_variables', updated);
                                      }
                                    }}
                                    className={`w-32 shrink-0 rounded-lg border px-2 py-1 text-[11px] font-medium focus:outline-none transition-colors ${
                                      isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-300 text-slate-800'
                                    }`}
                                  >
                                    <option value="">Select variable...</option>
                                    {apiVariables.length > 0 && (
                                      <optgroup label="API Response Data">
                                        {apiVariables.map((v) => (
                                          <option key={v} value={`{{${v}}}`}>{`{{${v}}}`}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {questionVariables.length > 0 && (
                                      <optgroup label="Question Inputs">
                                        {questionVariables.map((v) => (
                                          <option key={v} value={`{{${v}}}`}>{`{{${v}}}`}</option>
                                        ))}
                                      </optgroup>
                                    )}
                                    {DYNAMIC_VARIABLE_GROUPS.map((grp) => (
                                      <optgroup key={grp.group} label={grp.group}>
                                        {grp.variables.map((v) => (
                                          <option key={v.value} value={`{{${v.value}}}`}>
                                            {v.label} ({`{{${v.value}}}`})
                                          </option>
                                        ))}
                                      </optgroup>
                                    ))}
                                    <optgroup label="Contact Fields">
                                      <option value="{{contact.name}}">contact.name</option>
                                      <option value="{{contact.first_name}}">contact.first_name</option>
                                      <option value="{{contact.phone}}">contact.phone</option>
                                    </optgroup>
                                  </select>
                                </div>

                                {apiVariables.length > 0 && (
                                  <div className="flex flex-wrap gap-1 pt-0.5">
                                    {apiVariables.map((v) => (
                                      <button
                                        key={v}
                                        type="button"
                                        onClick={() => {
                                          const updated = { ...(data.template_button_variables || {}), [btn.index]: `{{${v}}}` };
                                          handleChange('template_button_variables', updated);
                                        }}
                                        className={`rounded px-1.5 py-0.2 text-[9px] font-mono border transition-all ${
                                          currentVal === `{{${v}}}`
                                            ? 'bg-emerald-500 text-white border-emerald-600 font-bold'
                                            : (isDark ? 'bg-slate-900/80 text-slate-300 border-slate-700 hover:bg-slate-800' : 'bg-white text-slate-700 border-slate-200 hover:bg-emerald-50')
                                        }`}
                                      >
                                        + {`{{${v}}}`}
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Carousel Cards Section (Media Card Carousel Templates) */}
                    {carouselCards.length > 0 && (
                      <div className={`p-2.5 rounded-lg border space-y-3 ${
                        isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-white border-slate-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-bold text-amber-500 flex items-center gap-1.5">
                            <Sparkles className="h-3.5 w-3.5 text-amber-400" />
                            <span>Carousel Cards ({carouselCards.length} Cards)</span>
                          </span>
                          <span className="text-[10px] text-slate-400">Media & Variables</span>
                        </div>

                        <div className="space-y-3">
                          {carouselCards.map((card, cIdx) => {
                            const cardMediaVal = (data.carousel_media_variables || {})[cIdx] !== undefined 
                              ? data.carousel_media_variables[cIdx] 
                              : (card.header_url || card.media_url || '');
                            const cBody = card.body || '';
                            const cardMatches = cBody.match(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g) || [];
                            const cardPlaceholders = Array.from(new Set(cardMatches.map(m => m.replace(/[\{\}\s]/g, ''))));

                            return (
                              <div key={cIdx} className={`p-2.5 rounded-lg border space-y-2.5 ${
                                isDark ? 'bg-slate-800/70 border-slate-700' : 'bg-slate-50 border-slate-200'
                              }`}>
                                <div className="flex items-center justify-between">
                                  <span className="text-[11px] font-bold text-emerald-500 flex items-center gap-1.5">
                                    <span>Card #{cIdx + 1}</span>
                                    <span className="text-[9px] uppercase px-1 py-0.2 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                                      {card.header_type || 'IMAGE'}
                                    </span>
                                  </span>
                                  {cardMediaVal && (
                                    <span className="text-[9px] text-slate-400 font-mono truncate max-w-[120px]">
                                      {cardMediaVal}
                                    </span>
                                  )}
                                </div>

                                {/* Card Media Input */}
                                <div>
                                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">Card Header Media URL or Variable</label>
                                  <div className="flex gap-1.5">
                                    <input
                                      type="text"
                                      placeholder="https://... or {{product_image}}"
                                      value={cardMediaVal}
                                      onChange={(e) => {
                                        const updated = { ...(data.carousel_media_variables || {}), [cIdx]: e.target.value };
                                        handleChange('carousel_media_variables', updated);
                                      }}
                                      className={`${inputCls} font-mono text-[11px]`}
                                    />
                                  </div>

                                  {/* Quick Chips for Card Media */}
                                  {apiVariables.length > 0 && (
                                    <div className="flex flex-wrap gap-1 pt-1">
                                      {apiVariables.map(v => (
                                        <button
                                          key={v}
                                          type="button"
                                          onClick={() => {
                                            const updated = { ...(data.carousel_media_variables || {}), [cIdx]: `{{${v}}}` };
                                            handleChange('carousel_media_variables', updated);
                                          }}
                                          className={`rounded px-1.5 py-0.2 text-[9px] font-mono border transition-all ${
                                            cardMediaVal === `{{${v}}}`
                                              ? 'bg-amber-500 text-white border-amber-600 font-bold'
                                              : (isDark ? 'bg-slate-900/80 text-amber-300 border-slate-700 hover:bg-slate-800' : 'bg-white text-amber-800 border-amber-200 hover:bg-amber-50')
                                          }`}
                                        >
                                          + {`{{${v}}}`}
                                        </button>
                                      ))}
                                    </div>
                                  )}

                                  {/* Mini Thumbnail */}
                                  {cardMediaVal && !cardMediaVal.startsWith('{{') && (
                                    <div className="mt-1.5 rounded border overflow-hidden max-h-20 bg-black/10 flex items-center justify-center p-0.5">
                                      <img src={cardMediaVal} alt={`Card ${cIdx + 1}`} className="max-h-16 object-contain rounded" onError={(e) => { e.target.style.display = 'none'; }} />
                                    </div>
                                  )}
                                </div>

                                {/* Card Body Text */}
                                <div>
                                  <label className="block text-[10px] font-semibold text-slate-400 mb-1">Card Body</label>
                                  <div className={`p-1.5 rounded border text-[11px] leading-relaxed whitespace-pre-wrap ${
                                    isDark ? 'bg-slate-900/70 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-700'
                                  }`}>
                                    {cBody || 'No text'}
                                  </div>

                                  {/* Card Body Variables Mapping */}
                                  {cardPlaceholders.length > 0 && (
                                    <div className="space-y-1.5 mt-2 pt-2 border-t border-slate-700/40">
                                      <span className="text-[10px] font-bold text-amber-400 block">Map Card Body Variables:</span>
                                      {cardPlaceholders.map(ph => {
                                        const vKey = `${cIdx}_${ph}`;
                                        const currentVal = (data.carousel_body_variables || {})[vKey] || '';
                                        return (
                                          <div key={ph} className="flex items-center gap-1.5">
                                            <span className="text-[10px] font-mono font-bold text-emerald-400 w-12">{`{{${ph}}}`}:</span>
                                            <input
                                              type="text"
                                              placeholder="e.g. {{item_name}}"
                                              value={currentVal}
                                              onChange={(e) => {
                                                const updated = { ...(data.carousel_body_variables || {}), [vKey]: e.target.value };
                                                handleChange('carousel_body_variables', updated);
                                              }}
                                              className={`${inputCls} font-mono text-[11px] py-1`}
                                            />
                                            {apiVariables.length > 0 && (
                                              <select
                                                value=""
                                                onChange={(e) => {
                                                  if (e.target.value) {
                                                    const updated = { ...(data.carousel_body_variables || {}), [vKey]: e.target.value };
                                                    handleChange('carousel_body_variables', updated);
                                                  }
                                                }}
                                                className="w-24 text-[10px] rounded border py-1 px-1 bg-slate-900 border-slate-700 text-slate-200"
                                              >
                                                <option value="">+ Variable</option>
                                                {apiVariables.map(v => <option key={v} value={`{{${v}}}`}>{v}</option>)}
                                              </select>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                {/* Card Buttons */}
                                {Array.isArray(card.buttons) && card.buttons.length > 0 && (
                                  <div className="pt-1">
                                    <span className="text-[10px] text-slate-400 font-semibold block mb-1">Card Buttons:</span>
                                    <div className="space-y-1">
                                      {card.buttons.map((b, bIdx) => (
                                        <div key={bIdx} className="flex items-center justify-between text-[11px] px-2 py-1 rounded bg-black/10 border border-slate-700/30">
                                          <span className="font-semibold text-slate-300 flex items-center gap-1.5">
                                            <CircleDot className="h-2.5 w-2.5 text-blue-400" />
                                            {b.text || b.title}
                                          </span>
                                          <span className="text-[9px] uppercase px-1 rounded bg-slate-700 text-slate-400">{b.type}</span>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Live Template Body Preview with Highlighted Variables */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className={labelCls}>Template Preview (With Mappings)</label>
                        <span className="text-[10px] text-slate-400">Live preview</span>
                      </div>
                      <div className={`p-2.5 rounded-lg border text-xs leading-relaxed whitespace-pre-wrap ${
                        isDark ? 'bg-slate-900 border-slate-700 text-slate-200' : 'bg-white border-slate-200 text-slate-800'
                      }`}>
                        {bodyContent ? (
                          bodyContent.split(/(\{\{\s*[a-zA-Z0-9_]+\s*\}\})/g).map((part, idx) => {
                            const match = part.match(/^\{\{\s*([a-zA-Z0-9_]+)\s*\}\}$/);
                            if (match) {
                              const ph = match[1];
                              const mapped = (data.template_variables || {})[ph];
                              if (mapped) {
                                return (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center px-1.5 py-0.2 rounded font-mono font-bold text-[11px] bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30"
                                    title={`Placeholder {{${ph}}} mapped to ${mapped}`}
                                  >
                                    {mapped}
                                  </span>
                                );
                              }
                              return (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-1.5 py-0.2 rounded font-mono font-bold text-[11px] bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30"
                                  title={`Unmapped placeholder {{${ph}}}`}
                                >
                                  {part}
                                </span>
                              );
                            }
                            return <span key={idx}>{part}</span>;
                          })
                        ) : (
                          'No template content selected yet.'
                        )}

                        {/* Horizontal WhatsApp Carousel Preview */}
                        {carouselCards.length > 0 && (
                          <div className="mt-3 pt-2.5 border-t border-slate-700/50">
                            <span className="text-[10px] font-bold text-amber-400 flex items-center gap-1.5 mb-1.5">
                              <Layers className="h-3.5 w-3.5" />
                              WhatsApp Carousel Cards (Horizontal Scroll →):
                            </span>
                            <div className="flex gap-2.5 overflow-x-auto pb-2 scrollbar-thin">
                              {carouselCards.map((c, i) => {
                                const mediaUrl = (data.carousel_media_variables || {})[i] !== undefined
                                  ? data.carousel_media_variables[i]
                                  : (c.header_url || c.media_url || 'https://picsum.photos/300/180');
                                return (
                                  <div key={i} className="min-w-[170px] max-w-[170px] rounded-lg border border-slate-300/40 dark:border-slate-700 bg-white dark:bg-[#1f2c34] overflow-hidden shadow shrink-0 text-left">
                                    <div className="h-20 w-full bg-slate-800 overflow-hidden relative">
                                      {mediaUrl && mediaUrl.startsWith('{{') ? (
                                        <div className="h-full w-full flex items-center justify-center p-1 bg-amber-950/40 text-[10px] font-mono text-amber-300 text-center">
                                          {mediaUrl}
                                        </div>
                                      ) : mediaUrl ? (
                                        <img src={mediaUrl} alt={`Card ${i + 1}`} className="h-full w-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                                      ) : (
                                        <div className="h-full w-full flex items-center justify-center text-[10px] text-slate-500">No Image</div>
                                      )}
                                    </div>
                                    <div className="p-2 space-y-1.5">
                                      <div className="text-[10px] font-bold text-slate-800 dark:text-slate-100 line-clamp-2">
                                        {c.body || `Card ${i + 1}`}
                                      </div>
                                      {Array.isArray(c.buttons) && c.buttons.map((b, bi) => (
                                        <div key={bi} className="text-center text-[10px] font-semibold py-1 px-1 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 truncate">
                                          {b.text || b.title}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()
            ) : (
              <>
                <div>
                  <label className={labelCls}>Header Media Type</label>
                  <select
                    value={data.headerType || 'NONE'}
                    onChange={(e) => handleChange('headerType', e.target.value)}
                    className={`${inputCls} focus:border-emerald-500`}
                  >
                    <option value="NONE">None (Text Only)</option>
                    <option value="IMAGE">Image (JPG, PNG)</option>
                    <option value="DOCUMENT">Document (PDF)</option>
                    <option value="VIDEO">Video (MP4)</option>
                  </select>
                </div>

                {data.headerType && data.headerType !== 'NONE' && (
                  <div>
                    <label className={labelCls}>Media URL</label>
                    <input
                      type="text"
                      placeholder="https://example.com/banner.png"
                      value={data.mediaUrl || ''}
                      onChange={(e) => handleChange('mediaUrl', e.target.value)}
                      className={`${inputCls} focus:border-emerald-500`}
                    />
                  </div>
                )}

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={labelCls}>Message Body *</label>
                    <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Click variable to insert
                    </span>
                  </div>

                  {/* Dynamic Variable Chips Toolbar */}
                  <div className={`mb-2 p-2.5 rounded-lg border text-xs space-y-2 ${
                    isDark ? 'bg-slate-900/90 border-slate-700' : 'bg-slate-50 border-slate-200'
                  }`}>
                    <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      <span className="flex items-center gap-1.5 text-amber-500 font-bold">
                        <Zap className="h-3.5 w-3.5 text-amber-400" />
                        Insert Collection Variables:
                      </span>
                      <span className="text-[10px] text-slate-400 font-normal">
                        Click to insert in message
                      </span>
                    </div>

                    {/* Category Tabs */}
                    <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-thin">
                      {[...DYNAMIC_VARIABLE_GROUPS.map((g) => g.group), 'Flow Inputs'].map((cat) => {
                        const isActive = activeVarCategory === cat;
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setActiveVarCategory(cat)}
                            className={`px-2 py-0.5 rounded text-[10px] font-bold whitespace-nowrap transition-all ${
                              isActive
                                ? 'bg-[#dc2742] text-white shadow-xs'
                                : isDark
                                ? 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                : 'bg-slate-200/80 text-slate-700 hover:bg-slate-300'
                            }`}
                          >
                            {cat}
                          </button>
                        );
                      })}
                    </div>

                    {/* Variable Pills for Active Category */}
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto pt-0.5">
                      {activeVarCategory === 'Flow Inputs' ? (
                        <>
                          {[
                            { label: 'Name', val: '{{contact.name}}' },
                            { label: 'First Name', val: '{{contact.first_name}}' },
                            { label: 'Phone', val: '{{contact.phone}}' },
                          ].map((item) => (
                            <button
                              key={item.val}
                              type="button"
                              onClick={() => insertVariableIntoMessage(item.val)}
                              className={`rounded px-1.5 py-0.5 text-[10px] font-mono font-medium border transition-colors ${
                                isDark
                                  ? 'bg-emerald-950/40 border-emerald-800/60 text-emerald-300 hover:bg-emerald-900/60'
                                  : 'bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100'
                              }`}
                              title={`Insert ${item.val}`}
                            >
                              + {item.val}
                            </button>
                          ))}
                          {allAttributes.map((attr) => (
                            <button
                              key={attr}
                              type="button"
                              onClick={() => insertVariableIntoMessage(`{{${attr}}}`)}
                              className={`rounded px-1.5 py-0.5 text-[10px] font-mono font-bold border transition-colors ${
                                flowVariables.includes(attr)
                                  ? isDark
                                    ? 'bg-sky-950/50 border-sky-700/60 text-sky-300 hover:bg-sky-900/70'
                                    : 'bg-sky-50 border-sky-300 text-sky-800 hover:bg-sky-100'
                                  : isDark
                                  ? 'bg-purple-950/40 border-purple-800/60 text-purple-300 hover:bg-purple-900/60'
                                  : 'bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100'
                              }`}
                              title={`Insert {{${attr}}}`}
                            >
                              + &#123;&#123;{attr}&#125;&#125;
                            </button>
                          ))}
                        </>
                      ) : (
                        (DYNAMIC_VARIABLE_GROUPS.find((g) => g.group === activeVarCategory)?.variables || []).map((v) => (
                          <button
                            key={v.value}
                            type="button"
                            onClick={() => insertVariableIntoMessage(`{{${v.value}}}`)}
                            className={`rounded px-2 py-0.5 text-[10px] font-mono font-medium border transition-all hover:scale-105 ${
                              isDark
                                ? 'bg-slate-800/90 border-slate-700 text-emerald-300 hover:bg-emerald-950/40 hover:border-emerald-700'
                                : 'bg-white border-slate-300 text-emerald-800 hover:bg-emerald-50 hover:border-emerald-300 shadow-2xs'
                            }`}
                            title={`${v.label} (e.g. "${v.example}")`}
                          >
                            + {`{{${v.value}}}`}
                            <span className="text-[9px] text-slate-400 font-sans ml-1">({v.label})</span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>

                  <textarea
                    id={`msg-body-textarea-${id}`}
                    rows={4}
                    value={data.body || ''}
                    onChange={(e) => handleChange('body', e.target.value)}
                    placeholder="Type your WhatsApp message..."
                    className={`${inputCls} focus:border-emerald-500 p-3`}
                  />
                </div>
              </>
            )}

            {/* Interactive Mode: Quick Buttons vs WhatsApp List Menu */}
            <div className="pt-2 border-t border-slate-700/30">
              <label className={labelCls}>Interaction Type</label>
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => handleChange('interactiveType', 'buttons')}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold border text-center transition-all flex items-center justify-center gap-1.5 ${
                    (data.interactiveType || 'buttons') !== 'list'
                      ? 'bg-emerald-600 text-white border-emerald-500 shadow-sm'
                      : (isDark ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200')
                  }`}
                >
                  <span>Buttons (Max 3)</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onUpdateNode(id, {
                      ...data,
                      interactiveType: 'list',
                      listSource: data.listSource || 'packages',
                      listCtaText: data.listCtaText || 'View Packages',
                      listSectionTitle: data.listSectionTitle || 'Available Packages',
                      listVariable: data.listVariable || 'selected_package',
                    });
                  }}
                  className={`py-1.5 px-2 rounded-lg text-xs font-semibold border text-center transition-all flex items-center justify-center gap-1.5 ${
                    data.interactiveType === 'list'
                      ? 'bg-teal-600 text-white border-teal-500 shadow-sm'
                      : (isDark ? 'bg-slate-800 text-slate-300 border-slate-700' : 'bg-slate-100 text-slate-700 border-slate-200')
                  }`}
                >
                  <ListFilter className="h-3.5 w-3.5" />
                  <span>List Menu (Max 10)</span>
                </button>
              </div>
            </div>

            {data.interactiveType === 'list' ? (
              /* WhatsApp Native Interactive List Settings */
              <div className="space-y-3.5 p-3 rounded-xl border border-teal-500/30 bg-teal-500/5">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-teal-500 flex items-center gap-1.5">
                    <ListFilter className="h-3.5 w-3.5" />
                    WhatsApp Interactive List Menu
                  </span>
                  <span className="text-[10px] px-1.5 py-0.2 rounded font-mono font-bold bg-teal-500/15 text-teal-400">
                    Max 10 Items
                  </span>
                </div>

                {/* List CTA Button Label */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={labelCls}>Menu Button CTA (Max 20 chars) *</label>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {(data.listCtaText || '').length}/20
                    </span>
                  </div>
                  <input
                    type="text"
                    maxLength={20}
                    placeholder="e.g. View Packages"
                    value={data.listCtaText || ''}
                    onChange={(e) => handleChange('listCtaText', e.target.value)}
                    className={`${inputCls} font-medium focus:border-teal-500`}
                  />
                </div>

                {/* List Section Title */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className={labelCls}>Section Title (Max 24 chars)</label>
                    <span className="text-[10px] text-slate-400 font-mono">
                      {(data.listSectionTitle || '').length}/24
                    </span>
                  </div>
                  <input
                    type="text"
                    maxLength={24}
                    placeholder="e.g. Available Packages"
                    value={data.listSectionTitle || ''}
                    onChange={(e) => handleChange('listSectionTitle', e.target.value)}
                    className={`${inputCls} focus:border-teal-500`}
                  />
                </div>

                {/* Data Source Selector */}
                <div>
                  <label className={labelCls}>Menu Data Source</label>
                  <div className="grid grid-cols-2 gap-1.5 mt-1">
                    {[
                      { key: 'packages', label: 'Gym Packages (10)', cta: 'View Packages', sec: 'Available Packages', v: 'selected_package' },
                      { key: 'classes', label: 'Gym Classes (10)', cta: 'View Classes', sec: 'Upcoming Classes', v: 'selected_class' },
                      { key: 'products', label: 'Gym Products (10)', cta: 'View Products', sec: 'Fitness Store', v: 'selected_product' },
                      { key: 'branches', label: 'Gym Branches', cta: 'Select Branch', sec: 'Our Locations', v: 'selected_branch_id' },
                      { key: 'custom', label: 'Custom Items', cta: 'Select Option', sec: 'Choose One', v: 'selected_item' },
                    ].map((src) => {
                      const isSel = (data.listSource || 'packages') === src.key;
                      return (
                        <button
                          key={src.key}
                          type="button"
                          onClick={() => {
                            onUpdateNode(id, {
                              ...data,
                              listSource: src.key,
                              listCtaText: data.listCtaText || src.cta,
                              listSectionTitle: data.listSectionTitle || src.sec,
                              listVariable: data.listVariable || src.v,
                            });
                          }}
                          className={`py-1.5 px-2 rounded-lg text-[11px] font-semibold border text-center transition-all ${
                            isSel
                              ? 'bg-teal-600 text-white border-teal-500 shadow-xs'
                              : isDark
                              ? 'bg-slate-800 text-slate-300 border-slate-700 hover:bg-slate-750'
                              : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                          }`}
                        >
                          {src.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Branch Scope Filter */}
                {data.listSource !== 'branches' && data.listSource !== 'custom' && (
                  <div>
                    <label className={labelCls}>Branch Scope</label>
                    <select
                      value={data.branchScope || 'dynamic'}
                      onChange={(e) => handleChange('branchScope', e.target.value)}
                      className={`${inputCls} focus:border-teal-500`}
                    >
                      <option value="dynamic">🎯 Dynamic (User's Branch / Lead Selection)</option>
                      <option value="all">🌐 All Branches (Universal List)</option>
                      {(liveCollections?.branches || []).map((br) => (
                        <option key={br.id} value={br.id}>
                          📍 Branch: {br.title || br.name}
                        </option>
                      ))}
                    </select>
                    <span className="text-[10px] text-slate-400 mt-1 block">
                      {(!data.branchScope || data.branchScope === 'dynamic')
                        ? "Filters automatically to the member's assigned branch or whichever branch the lead picked."
                        : data.branchScope === 'all'
                        ? "Shows items across all branches of your gym company."
                        : "Always shows only items belonging to this specific branch."}
                    </span>
                  </div>
                )}

                {/* Target Variable to Store Selection */}
                <div>
                  <label className={labelCls}>Save Customer Selection To Variable</label>
                  <input
                    type="text"
                    placeholder="e.g. selected_package"
                    value={data.listVariable || ''}
                    onChange={(e) => handleChange('listVariable', e.target.value)}
                    className={`${inputCls} font-mono focus:border-teal-500`}
                  />
                  <div className="flex gap-1 mt-1.5">
                    {['selected_package', 'selected_class', 'selected_product', 'user_choice'].map((vName) => (
                      <button
                        key={vName}
                        type="button"
                        onClick={() => handleChange('listVariable', vName)}
                        className={`text-[9px] font-mono px-1.5 py-0.5 rounded border transition-colors ${
                          data.listVariable === vName
                            ? 'bg-teal-500 text-white border-teal-600 font-bold'
                            : isDark
                            ? 'bg-slate-800 text-slate-300 border-slate-700'
                            : 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        {vName}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Items Editor (if listSource === 'custom') */}
                {data.listSource === 'custom' && (
                  <div className="space-y-2 pt-2 border-t border-slate-700/30">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-bold text-slate-300">
                        Custom Items ({(data.listCustomItems || []).length}/10)
                      </span>
                      {(data.listCustomItems || []).length < 10 && (
                        <button
                          type="button"
                          onClick={handleAddCustomListItem}
                          className="flex items-center gap-1 text-[11px] font-semibold text-teal-400 hover:text-teal-300"
                        >
                          <Plus className="h-3 w-3" /> Add Item
                        </button>
                      )}
                    </div>

                    {/* Canvas Branching Toggle */}
                    <label className="flex items-center gap-2 cursor-pointer pt-1 pb-1">
                      <input
                        type="checkbox"
                        checked={!!data.listBranching}
                        onChange={(e) => handleChange('listBranching', e.target.checked)}
                        className="rounded text-teal-500 focus:ring-teal-500"
                      />
                      <span className="text-[11px] text-slate-300">
                        Create individual branch handle for each item on canvas
                      </span>
                    </label>

                    {(data.listCustomItems || []).length === 0 ? (
                      <div className={`p-2.5 rounded-lg border text-center text-[11px] ${
                        isDark ? 'border-slate-800 bg-slate-900/40 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500'
                      }`}>
                        No custom items yet. Click <span className="font-semibold text-teal-400">+ Add Item</span> to create up to 10 options.
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                        {(data.listCustomItems || []).map((item, idx) => (
                          <div key={item.id || idx} className={`p-2 rounded-lg border space-y-1.5 ${
                            isDark ? 'bg-slate-800/80 border-slate-700' : 'bg-slate-50 border-slate-200'
                          }`}>
                            <div className="flex items-center justify-between gap-1.5">
                              <span className="text-[10px] font-bold text-teal-400 font-mono">#{idx + 1}</span>
                              <input
                                type="text"
                                maxLength={24}
                                placeholder="Item Title (Max 24)"
                                value={item.title || ''}
                                onChange={(e) => handleUpdateCustomListItem(idx, 'title', e.target.value)}
                                className={`flex-1 rounded border px-2 py-1 text-xs focus:outline-none focus:border-teal-500 ${
                                  isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-300 text-slate-900'
                                }`}
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveCustomListItem(idx)}
                                className="text-slate-400 hover:text-red-400 p-1"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <input
                              type="text"
                              maxLength={72}
                              placeholder="Description / Price (Max 72, optional)"
                              value={item.description || ''}
                              onChange={(e) => handleUpdateCustomListItem(idx, 'description', e.target.value)}
                              className={`w-full rounded border px-2 py-1 text-[11px] focus:outline-none focus:border-teal-500 ${
                                isDark ? 'bg-slate-900/60 border-slate-700 text-slate-300' : 'bg-white border-slate-300 text-slate-700'
                              }`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* 10-Item WhatsApp Popup Preview */}
                {(() => {
                  const branchFilter = data.branchScope === 'all' || data.branchScope === 'dynamic' ? null : data.branchScope;
                  const resolvedItems = getDynamicListItems(data.listSource || 'packages', data.listCustomItems || [], liveCollections, branchFilter);
                  return (
                    <div className="space-y-1.5 pt-2 border-t border-slate-700/30">
                      <div className="flex items-center justify-between text-[11px] font-bold text-slate-300">
                        <span>WhatsApp Menu Preview</span>
                        <span className="text-[10px] text-teal-400">
                          {resolvedItems.length} Items Available
                        </span>
                      </div>
                      <div className={`p-2.5 rounded-lg border max-h-48 overflow-y-auto space-y-1.5 text-xs ${
                        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
                      }`}>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 border-b pb-1 mb-1 border-slate-700/40">
                          {data.listSectionTitle || 'Available Options'}
                        </div>
                        {resolvedItems.length === 0 ? (
                          <div className={`p-2 text-center text-[11px] italic ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                            {data.listSource === 'custom'
                              ? 'No custom items added yet.'
                              : `No ${data.listSource} found in Firestore for this company.`}
                          </div>
                        ) : (
                          resolvedItems.map((row, rIdx) => (
                            <div key={row.id || rIdx} className="flex items-center justify-between py-1 border-b border-slate-700/20 last:border-0">
                              <div className="min-w-0 pr-2">
                                <div className="font-semibold text-slate-200 text-[11px] truncate">{row.title}</div>
                                {row.description && (
                                  <div className="text-[10px] text-slate-400 truncate">{row.description}</div>
                                )}
                              </div>
                              <span className="h-3 w-3 rounded-full border border-teal-500/60 shrink-0" />
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              /* Quick Reply Buttons (Max 3) */
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls}>Quick Reply Buttons ({data.buttons?.length || 0}/3)</label>
                  {(data.buttons?.length || 0) < 3 && (
                    <button
                      type="button"
                      onClick={handleAddButton}
                      className="flex items-center gap-1 text-[11px] font-semibold text-emerald-500 hover:text-emerald-600"
                    >
                      <Plus className="h-3 w-3" /> Add Button
                    </button>
                  )}
                </div>
                {(data.buttons || []).length === 0 ? (
                  <div className={`p-2.5 rounded-lg border text-center text-[11px] ${
                    isDark ? 'border-slate-800 bg-slate-900/40 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}>
                    No quick reply buttons added. Click <span className="font-semibold text-emerald-500">+ Add Button</span> if you want interactive buttons.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(data.buttons || []).map((btn, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <input
                          type="text"
                          maxLength={20}
                          value={btn.title || ''}
                          onChange={(e) => handleUpdateButton(idx, e.target.value)}
                          placeholder={`Button ${idx + 1}`}
                          className={`flex-1 rounded-md border px-2.5 py-1.5 text-xs focus:outline-none focus:border-emerald-500 ${
                            isDark ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-300 bg-slate-50 text-slate-900'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveButton(idx)}
                          className="rounded p-1.5 text-slate-400 hover:text-rose-500 transition-colors"
                          title="Remove Button"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Question Node Specifics */}
        {type === 'questionNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Question to Ask</label>
              <textarea
                rows={3}
                value={data.question || ''}
                onChange={(e) => handleChange('question', e.target.value)}
                placeholder="e.g. Which city or course are you interested in?"
                className={`${inputCls} focus:border-sky-500 p-2.5`}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>Save Answer to Variable Name</label>
                <span className="text-[10px] text-sky-500 font-semibold">Auto-saves to Contact Attributes</span>
              </div>
              <input
                type="text"
                list="contact-custom-attributes-list"
                value={data.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="Select existing attribute or type new (e.g. user_city, age)..."
                className={`${inputCls} focus:border-sky-500 font-mono`}
              />
              <datalist id="contact-custom-attributes-list">
                {allAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr} (Contact Attribute)
                  </option>
                ))}
              </datalist>

              {allAttributes.length > 0 && (
                <div className="mt-2 space-y-1">
                  <div className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                    Select from Existing Contact Attributes:
                  </div>
                  <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                    {allAttributes.map((attr) => (
                      <button
                        key={attr}
                        type="button"
                        onClick={() => handleChange('variable', attr)}
                        className={`rounded px-2 py-0.5 text-[11px] font-mono border transition-all ${
                          data.variable === attr
                            ? 'bg-sky-500 text-white border-sky-400 font-bold shadow-xs'
                            : isDark
                            ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-sky-500 hover:text-white'
                            : 'bg-slate-100 border-slate-200 text-slate-700 hover:border-sky-500 hover:bg-sky-50'
                        }`}
                      >
                        {attr}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp Flow Node Specifics */}
        {type === 'whatsappFlowNode' && (
          <div className="space-y-4">
            <div>
              <label className={labelCls}>Select Meta WhatsApp Flow</label>
              <select
                value={data.flow_id || ''}
                onChange={(e) => {
                  const selVal = e.target.value;
                  const selected = flowsList.find(f => (f.flow_id === selVal || String(f.id) === selVal));
                  handleChange('flow_id', selVal);
                  if (selected) {
                    handleChange('flow_name', selected.name);
                  }
                }}
                className={`${inputCls} focus:border-teal-500`}
              >
                <option value="">-- Choose a WhatsApp Flow --</option>
                {flowsList.map((f) => (
                  <option key={f.id || f.flow_id} value={f.flow_id || f.id}>
                    {f.name} {f.status ? `(${f.status})` : ''}
                  </option>
                ))}
              </select>
              <div className="text-[10px] text-teal-500 mt-1">
                {flowsList.length > 0 
                  ? `Found ${flowsList.length} flow(s) in this workspace.`
                  : 'No flows found. Create one under WhatsApp Flows menu.'}
              </div>
            </div>

            <div>
              <label className={labelCls}>Message Header (Optional)</label>
              <input
                type="text"
                value={data.header || ''}
                onChange={(e) => handleChange('header', e.target.value)}
                placeholder="e.g. Consultation Form"
                className={`${inputCls} focus:border-teal-500`}
              />
            </div>

            <div>
              <label className={labelCls}>Message Body</label>
              <textarea
                rows={3}
                value={data.body || ''}
                onChange={(e) => handleChange('body', e.target.value)}
                placeholder="Please complete this quick form by tapping below:"
                className={`${inputCls} focus:border-teal-500`}
              />
            </div>

            <div>
              <label className={labelCls}>Button Text (CTA)</label>
              <input
                type="text"
                value={data.cta_text || ''}
                onChange={(e) => handleChange('cta_text', e.target.value)}
                placeholder="Open Form"
                maxLength={20}
                className={`${inputCls} focus:border-teal-500 font-medium`}
              />
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                The label that appears on the interactive button inside WhatsApp (Max 20 chars).
              </span>
            </div>

            <div>
              <label className={labelCls}>First Screen ID (Optional)</label>
              <input
                type="text"
                value={data.first_screen || ''}
                onChange={(e) => handleChange('first_screen', e.target.value)}
                placeholder="LEAD_SCREEN"
                className={`${inputCls} focus:border-teal-500 font-mono`}
              />
              <span className="text-[10px] text-slate-400 mt-0.5 block">
                The ID of the opening screen defined in your Flow JSON. Default: LEAD_SCREEN
              </span>
            </div>
          </div>
        )}

        {/* Condition Node Specifics */}
        {type === 'conditionNode' && (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>Check Variable or Tag</label>
                <span className="text-[10px] text-amber-500 font-semibold">Tag or Attribute</span>
              </div>
              <input
                type="text"
                list="condition-variables-list"
                value={data.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="e.g. tag, user_city, age..."
                className={`${inputCls} focus:border-amber-500 font-mono`}
              />
              <datalist id="condition-variables-list">
                <option value="tag">tag (Check Contact Tags)</option>
                {allAttributes.map((attr) => (
                  <option key={attr} value={attr}>
                    {attr}
                  </option>
                ))}
                {ALL_DYNAMIC_VARIABLES.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.group}: {v.label} ({v.value})
                  </option>
                ))}
              </datalist>

              <div className="mt-2 space-y-1">
                <div className={`text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Quick Select Variable:
                </div>
                <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                  <button
                    type="button"
                    onClick={() => handleChange('variable', 'tag')}
                    className={`rounded px-2 py-0.5 text-[11px] font-mono border transition-all ${
                      data.variable === 'tag'
                        ? 'bg-amber-500 text-white border-amber-400 font-bold shadow-xs'
                        : isDark
                        ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-amber-500'
                        : 'bg-slate-100 border-slate-200 text-slate-700 hover:border-amber-500'
                    }`}
                  >
                    tag
                  </button>
                  {allAttributes.map((attr) => (
                    <button
                      key={attr}
                      type="button"
                      onClick={() => handleChange('variable', attr)}
                      className={`rounded px-2 py-0.5 text-[11px] font-mono border transition-all ${
                        data.variable === attr
                          ? 'bg-amber-500 text-white border-amber-400 font-bold shadow-xs'
                          : isDark
                          ? 'bg-slate-800 border-slate-700 text-slate-300 hover:border-amber-500'
                          : 'bg-slate-100 border-slate-200 text-slate-700 hover:border-amber-500'
                      }`}
                    >
                      {attr}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className={labelCls}>Comparison Operator</label>
              <select
                value={data.operator || 'equals'}
                onChange={(e) => handleChange('operator', e.target.value)}
                className={`${inputCls} focus:border-amber-500`}
              >
                <option value="equals">Equals (Exact match)</option>
                <option value="contains">Contains (Partial text)</option>
                <option value="not_equals">Does Not Equal</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>Value to Match</label>
              <input
                type="text"
                value={data.value || ''}
                onChange={(e) => handleChange('value', e.target.value)}
                placeholder="e.g. VIP, Warm Lead, or text"
                className={`${inputCls} focus:border-amber-500`}
              />
            </div>

            <div className={`rounded-lg p-2.5 text-[11px] space-y-1 border ${
              isDark
                ? 'bg-amber-950/30 border-amber-900/40 text-amber-200/90'
                : 'bg-amber-50 border-amber-200 text-amber-900'
            }`}>
              <div className="font-semibold text-amber-500 flex items-center gap-1.5">
                <Lightbulb className="h-3.5 w-3.5" />
                <span>Branch Routing:</span>
              </div>
              <div>• <b>YES / TRUE Handle:</b> Followed if condition passes.</div>
              <div>• <b>NO / FALSE Handle:</b> Followed if condition fails.</div>
            </div>
          </div>
        )}

        {/* Action Node Specifics */}
        {type === 'actionNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Action Type</label>
              <select
                value={data.actionType || 'ADD_TAG'}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === 'ASSIGN_AGENT') {
                    onUpdateNode(id, {
                      ...data,
                      actionType: val,
                      tag: 'Human Agent Required'
                    });
                  } else {
                    handleChange('actionType', val);
                  }
                }}
                className={`${inputCls} focus:border-orange-500`}
              >
                <option value="ADD_TAG">Add Contact Tag</option>
                <option value="REMOVE_TAG">Remove Contact Tag</option>
                <option value="ASSIGN_AGENT">Handover to Human Agent (Notification)</option>
              </select>
            </div>

            {data.actionType === 'ASSIGN_AGENT' ? (
              <div className={`p-3 rounded-lg border text-xs leading-relaxed ${
                isDark ? 'bg-red-950/40 border-red-800/50 text-red-200' : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                <div className="font-bold flex items-center gap-1.5 text-red-500 mb-1">
                  <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                  <span>Support Team Alert</span>
                </div>
                <div>
                  When customer hits this block, the chat is automatically tagged <b>"Human Agent Required"</b> and an urgent notification alert is sent to all staff in Filament.
                </div>
              </div>
            ) : (
              <div>
                <label className={labelCls}>Tag Name</label>
                <input
                  type="text"
                  value={data.tag || ''}
                  onChange={(e) => handleChange('tag', e.target.value)}
                  placeholder="e.g. Hot Lead - Gym"
                  className={`${inputCls} focus:border-orange-500`}
                />
              </div>
            )}
          </div>
        )}

        {/* Delay Node Specifics */}
        {type === 'delayNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Wait Duration</label>
              <input
                type="number"
                min="1"
                value={data.duration || '24'}
                onChange={(e) => handleChange('duration', e.target.value)}
                className={`${inputCls} focus:border-teal-500`}
              />
            </div>
            <div>
              <label className={labelCls}>Unit</label>
              <select
                value={data.unit || 'hours'}
                onChange={(e) => handleChange('unit', e.target.value)}
                className={`${inputCls} focus:border-teal-500`}
              >
                <option value="minutes">Minutes</option>
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
          </div>
        )}

        {/* Date Time Node Specifics */}
        {type === 'dateTimeNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>Prompt Message</label>
              <textarea
                rows={3}
                value={data.prompt || ''}
                onChange={(e) => handleChange('prompt', e.target.value)}
                placeholder="Please select your convenient date & time slot:"
                className={`${inputCls} focus:border-indigo-500 p-2.5`}
              />
            </div>

            <div>
              <label className={labelCls}>Date Options (comma-separated)</label>
              <input
                type="text"
                value={(data.dateOptions || ['Today', 'Tomorrow', 'Next Business Day']).join(', ')}
                onChange={(e) => handleChange('dateOptions', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="Today, Tomorrow, Monday"
                className={`${inputCls} focus:border-indigo-500`}
              />
            </div>

            <div>
              <label className={labelCls}>Available Time Slots (comma-separated)</label>
              <input
                type="text"
                value={(data.slots || ['10:00 AM - 12:00 PM', '02:00 PM - 04:00 PM', '05:00 PM - 07:00 PM']).join(', ')}
                onChange={(e) => handleChange('slots', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                placeholder="10:00 AM - 12:00 PM, 02:00 PM - 04:00 PM, 05:00 PM - 07:00 PM"
                className={`${inputCls} focus:border-indigo-500`}
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className={labelCls}>Save Selected Slot to Variable</label>
                <span className="text-[10px] text-indigo-500 font-semibold">Auto-saves to Contact</span>
              </div>
              <input
                type="text"
                list="contact-custom-attributes-list"
                value={data.variable || ''}
                onChange={(e) => handleChange('variable', e.target.value)}
                placeholder="e.g. appointment_slot"
                className={`${inputCls} focus:border-indigo-500 font-mono`}
              />
            </div>
          </div>
        )}

        {/* External API / Webhook Node Specifics */}
        {type === 'apiNode' && (
          <div className="space-y-3">
            <div>
              <label className={labelCls}>HTTP Method</label>
              <select
                value={data.method || 'GET'}
                onChange={(e) => handleChange('method', e.target.value)}
                className={`${inputCls} focus:border-violet-500 font-bold`}
              >
                <option value="GET">GET (Fetch Delivery / Data)</option>
                <option value="POST">POST (Create Order / Send Payload)</option>
                <option value="PUT">PUT (Update External Record)</option>
                <option value="DELETE">DELETE</option>
              </select>
            </div>

            <div>
              <label className={labelCls}>Endpoint URL *</label>
              <input
                type="text"
                value={data.url || ''}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://api.example.com/delivery/status/{{phone}}"
                className={`${inputCls} focus:border-violet-500 font-mono`}
              />
              <span className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Tip: Interpolate variables like <code>&#123;&#123;contact.phone&#125;&#125;</code> or <code>&#123;&#123;pincode&#125;&#125;</code>
              </span>
            </div>

            {/* Authentication Settings */}
            <div className={`p-3 rounded-xl border space-y-2.5 ${
              isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                  <Key className="h-3.5 w-3.5 text-violet-500" />
                  <span>Authentication</span>
                </label>
                <span className="text-[10px] text-slate-400">Optional</span>
              </div>

              <div>
                <select
                  value={data.authType || (data.authToken ? 'bearer' : 'none')}
                  onChange={(e) => {
                    const newType = e.target.value;
                    handleChange('authType', newType);
                    if (newType === 'none') {
                      handleChange('authToken', '');
                      handleChange('bearerToken', '');
                      handleChange('apiKeyValue', '');
                      handleChange('basicAuthUsername', '');
                      handleChange('basicAuthPassword', '');
                    }
                  }}
                  className={`${inputCls} focus:border-violet-500 font-semibold text-xs`}
                >
                  <option value="none">No Authentication</option>
                  <option value="apiKey">API Key</option>
                  <option value="basic">Basic Auth (Username / Password)</option>
                  <option value="bearer">Bearer Token</option>
                </select>
              </div>

              {/* Bearer Token Form */}
              {(data.authType === 'bearer' || (!data.authType && data.authToken)) && (
                <div className="space-y-1.5 pt-1">
                  <label className="text-[11px] font-medium text-slate-300">Bearer Token</label>
                  <input
                    type="text"
                    value={data.bearerToken !== undefined ? data.bearerToken : (data.authToken || '')}
                    onChange={(e) => {
                      handleChange('bearerToken', e.target.value);
                      handleChange('authToken', e.target.value);
                    }}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6..."
                    className={`${inputCls} focus:border-violet-500 font-mono text-[11px]`}
                  />
                  <div className="text-[10px] text-slate-400">
                    Sends header: <code>Authorization: Bearer &lt;token&gt;</code>
                  </div>
                </div>
              )}

              {/* API Key Form */}
              {data.authType === 'apiKey' && (
                <div className="space-y-2 pt-1">
                  <div>
                    <label className="text-[11px] font-medium text-slate-300">Key Name</label>
                    <input
                      type="text"
                      value={data.apiKeyName !== undefined ? data.apiKeyName : 'X-API-Key'}
                      onChange={(e) => handleChange('apiKeyName', e.target.value)}
                      placeholder="X-API-Key or apikey"
                      className={`${inputCls} focus:border-violet-500 font-mono text-[11px]`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-300">Key Value</label>
                    <input
                      type="text"
                      value={data.apiKeyValue || ''}
                      onChange={(e) => handleChange('apiKeyValue', e.target.value)}
                      placeholder="e.g. secret_api_key_12345"
                      className={`${inputCls} focus:border-violet-500 font-mono text-[11px]`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-300">Add To</label>
                    <select
                      value={data.apiKeyAddTo || 'header'}
                      onChange={(e) => handleChange('apiKeyAddTo', e.target.value)}
                      className={`${inputCls} focus:border-violet-500 text-xs`}
                    >
                      <option value="header">Request Header (e.g. X-API-Key: value)</option>
                      <option value="query">Query Parameter (e.g. ?apikey=value)</option>
                    </select>
                  </div>
                </div>
              )}

              {/* Basic Auth Form */}
              {data.authType === 'basic' && (
                <div className="space-y-2 pt-1">
                  <div>
                    <label className="text-[11px] font-medium text-slate-300">Username</label>
                    <input
                      type="text"
                      value={data.basicAuthUsername || ''}
                      onChange={(e) => handleChange('basicAuthUsername', e.target.value)}
                      placeholder="API username"
                      className={`${inputCls} focus:border-violet-500 text-xs`}
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-slate-300">Password</label>
                    <input
                      type="password"
                      value={data.basicAuthPassword || ''}
                      onChange={(e) => handleChange('basicAuthPassword', e.target.value)}
                      placeholder="••••••••"
                      className={`${inputCls} focus:border-violet-500 text-xs`}
                    />
                  </div>
                  <div className="text-[10px] text-slate-400">
                    Encodes as Base64 <code>Authorization: Basic &lt;credentials&gt;</code>
                  </div>
                </div>
              )}
            </div>

            {/* Custom Request Headers (Optional) */}
            <div className={`p-3 rounded-xl border space-y-2 ${
              isDark ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50 border-slate-200'
            }`}>
              <div className="flex items-center justify-between">
                <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Custom Headers</label>
                <button
                  type="button"
                  onClick={() => {
                    const headers = data.headers || [];
                    handleChange('headers', [...headers, { key: '', value: '' }]);
                  }}
                  className="flex items-center gap-1 text-[11px] font-semibold text-violet-500 hover:text-violet-600"
                >
                  <Plus className="h-3 w-3" /> Add Header
                </button>
              </div>

              {(data.headers || []).length > 0 ? (
                <div className="space-y-1.5">
                  {(data.headers || []).map((h, hIdx) => (
                    <div key={hIdx} className="flex items-center gap-1.5 w-full min-w-0">
                      <input
                        type="text"
                        placeholder="Header"
                        value={h.key || ''}
                        onChange={(e) => {
                          const newH = [...(data.headers || [])];
                          newH[hIdx] = { ...newH[hIdx], key: e.target.value };
                          handleChange('headers', newH);
                        }}
                        className={`w-1/2 min-w-0 rounded-md border px-2 py-1 text-[11px] font-mono ${
                          isDark ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-900'
                        }`}
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={h.value || ''}
                        onChange={(e) => {
                          const newH = [...(data.headers || [])];
                          newH[hIdx] = { ...newH[hIdx], value: e.target.value };
                          handleChange('headers', newH);
                        }}
                        className={`w-1/2 min-w-0 rounded-md border px-2 py-1 text-[11px] font-mono ${
                          isDark ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-300 bg-white text-slate-900'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newH = (data.headers || []).filter((_, i) => i !== hIdx);
                          handleChange('headers', newH);
                        }}
                        className="shrink-0 p-1 text-slate-400 hover:text-rose-500 transition-colors"
                        title="Remove Header"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-[10px] text-slate-400 italic">No extra custom headers.</div>
              )}
            </div>

            {(data.method === 'POST' || data.method === 'PUT') && (
              <div>
                <label className={labelCls}>JSON Request Body</label>
                <textarea
                  rows={4}
                  value={data.body || ''}
                  onChange={(e) => handleChange('body', e.target.value)}
                  placeholder={`{\n  "phone": "{{contact.phone}}",\n  "pincode": "{{pincode}}"\n}`}
                  className={`${inputCls} focus:border-violet-500 font-mono text-[11px]`}
                />
              </div>
            )}

            {/* Variable Mapping */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls}>Extract Response to Variables</label>
                <button
                  type="button"
                  onClick={() => {
                    const mappings = data.mappings || [];
                    handleChange('mappings', [...mappings, { path: '', variable: '' }]);
                  }}
                  className="flex items-center gap-1 text-[11px] font-semibold text-violet-500 hover:text-violet-600"
                >
                  <Plus className="h-3 w-3" /> Add Mapping
                </button>
              </div>

              {(data.mappings || []).length === 0 ? (
                <div className={`p-2.5 rounded-lg border text-center text-[11px] ${
                  isDark ? 'border-slate-800 bg-slate-900/40 text-slate-400' : 'border-slate-200 bg-slate-50 text-slate-500'
                }`}>
                  No variables mapped yet. Click <span className="font-semibold text-violet-500">+ Add Mapping</span> to store JSON response fields.
                </div>
              ) : (
                <div className="space-y-2">
                {(data.mappings || []).map((m, idx) => (
                  <div key={idx} className="flex items-center gap-1.5 w-full min-w-0">
                    <input
                      type="text"
                      placeholder="JSON Path"
                      value={m.path || ''}
                      onChange={(e) => {
                        const newM = [...(data.mappings || [])];
                        newM[idx] = { ...newM[idx], path: e.target.value };
                        handleChange('mappings', newM);
                      }}
                      className={`flex-1 min-w-0 rounded-md border px-2 py-1.5 text-xs font-mono ${
                        isDark ? 'border-slate-700 bg-slate-800 text-white' : 'border-slate-300 bg-slate-50 text-slate-900'
                      }`}
                    />
                    <span className="text-slate-400 font-bold shrink-0">→</span>
                    <input
                      type="text"
                      list="contact-custom-attributes-list"
                      placeholder="Variable name"
                      value={m.variable || ''}
                      onChange={(e) => {
                        const newM = [...(data.mappings || [])];
                        newM[idx] = { ...newM[idx], variable: e.target.value };
                        handleChange('mappings', newM);
                      }}
                      className={`flex-1 min-w-0 rounded-md border px-2 py-1.5 text-xs font-mono font-semibold ${
                        isDark ? 'border-slate-700 bg-slate-800 text-violet-300' : 'border-slate-300 bg-slate-50 text-violet-700'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        const newM = (data.mappings || []).filter((_, i) => i !== idx);
                        handleChange('mappings', newM);
                      }}
                      className="shrink-0 p-1 text-slate-400 hover:text-rose-500 transition-colors"
                      title="Remove Mapping"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            </div>
          </div>
        )}

        {/* Delete Block Section */}
        {!isTrigger && onDeleteNode && (
          <div className={`pt-4 mt-6 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
            <button
              onClick={() => onDeleteNode(id)}
              className="w-full flex items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/10 py-2.5 px-3 text-xs font-bold text-red-500 hover:bg-red-500/20 hover:border-red-500/50 transition-all shadow-sm active:scale-98"
            >
              <Trash2 className="h-4 w-4" />
              <span>Delete this Block</span>
            </button>
            <p className={`mt-2 text-[10px] text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              Or click block on canvas and press <kbd className={`rounded border px-1.5 py-0.5 font-mono text-[9px] ${
                isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-100 border-slate-300 text-slate-700'
              }`}>Del</kbd> / <kbd className={`rounded border px-1.5 py-0.5 font-mono text-[9px] ${
                isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-100 border-slate-300 text-slate-700'
              }`}>Backspace</kbd>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
